/**
 * Doserne Project Detail — header + tabs (Info, Underlag, Kontroll, SJA, Progresjon).
 * Underlag/Kontroll/SJA tabs deep-link into the existing tools (project pre-filled).
 * Progresjon tab renders the photo timeline for this project.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, MapPin, User as UserIcon, Phone, Mail, Calendar,
  FileText, ClipboardList, ShieldAlert, Camera, ExternalLink, Building2, Plus,
  MessageCircle, Share2, Copy, RefreshCw, X, Trash2, CheckCircle2, Pencil, Clock,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";
import DoserneProgressTimeline from "./DoserneProgressTimeline";
import { usePlanLang, FlagBar, translateContent, FLAG_LABEL } from "./planTranslate";
import AdminDocsBox from "./AdminDocsBox";
import ProjectMaterialListsTab from "./ProjectMaterialListsTab";
import DoserneProjectJobs from "./DoserneProjectJobs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "info",      label: "Detaljer",   Icon: Building2 },
  { key: "progress",  label: "Progresjon", Icon: Camera },
  { key: "lister",    label: "Materiale",  Icon: ClipboardList },
  { key: "underlag",  label: "Underlag",   Icon: FileText },
  { key: "kontroll",  label: "Kontroll",   Icon: ClipboardList },
  { key: "sja",       label: "SJA",        Icon: ShieldAlert },
  { key: "timer",     label: "Ore",        Icon: Clock, adminOnly: true },
];

// External deep-link targets — full editor apps for each document type.
const CREATE_URLS = {
  underlag: (pid) => `/underlag?project_id=${pid}`,
  kontroll: (pid) => `/apps/kontroll?project_id=${pid}`,
  sja:      (pid) => `/apps/sja?project_id=${pid}`,
};
const OPEN_URLS = {
  // Prefer the edit-token URL — works for both draft and signed underlag
  // (admins can view + edit either). Falls back to slug/sign-token if the
  // edit token isn't in the payload for some reason.
  underlag: (d) => d.edit_token
    ? `/underlag/edit/${d.edit_token}`
    : (d.slug ? `/u/${d.slug}` : (d.sign_token ? `/underlag/sign/${d.sign_token}` : `/underlag?open=${d.underlag_id || d.id}`)),
  kontroll: (d) => d.edit_token ? `/apps/kontroll/edit/${d.edit_token}` : `/apps/kontroll?open=${d.id}`,
  sja:      (d) => d.edit_token ? `/apps/sja/edit/${d.edit_token}` : `/apps/sja?open=${d.id}`,
};

function KV({ Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className="text-slate-900">{value}</div>
      </div>
    </div>
  );
}

// Inline-editable address on the project Info tab. Admins can fill/update it
// here so the daily-plan "open in Maps" pin always resolves.
function AddressField({ value, projectId, isAdmin, authHeader, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(value || ""); }, [value]);

  if (!isAdmin) {
    if (!value) return null;
    return <KV Icon={MapPin} label="Adresse" value={value} />;
  }

  const save = async () => {
    setBusy(true);
    try {
      const next = val.trim();
      await axios.patch(`${API}/doserne/projects/${projectId}`, { address: next }, authHeader());
      onSaved?.(next);
      setEditing(false);
      toast.success("Adresse lagret");
    } catch {
      toast.error("Kunne ikke lagre adresse");
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="flex items-start gap-2 text-sm">
        <MapPin className="w-4 h-4 text-slate-400 mt-2 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Adresse</div>
          <div className="flex items-center gap-1.5">
            <input autoFocus value={val} onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(value || ""); setEditing(false); } }}
              placeholder="Storgata 10, Oslo" className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="project-address-input" />
            <button onClick={save} disabled={busy} className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold disabled:opacity-50" data-testid="project-address-save">{busy ? "…" : "Lagre"}</button>
            <button onClick={() => { setVal(value || ""); setEditing(false); }} className="px-1.5 py-1.5 text-slate-400"><X className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => setEditing(true)} className="flex items-start gap-2 text-sm text-left group w-full" data-testid="project-address-edit">
      <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">Adresse <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60" /></div>
        {value ? <div className="text-slate-900">{value}</div> : <div className="text-amber-600 font-semibold">Legg til adresse — for kart-pin</div>}
      </div>
    </button>
  );
}


// -----------------------------------------------------------------
// ClientPortalDialog — admin popover to create/revoke/copy the
// shareable, expiring, view-only client portal link for a project.
// -----------------------------------------------------------------
function ClientPortalDialog({ projectId, projectAddress, onClose, authHeader }) {
  const [kind, setKind] = useState("project"); // "project" | "customer"
  const [status, setStatus] = useState(null); // {active, token, expired, customer?}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ttl, setTtl] = useState(30);
  const [slug, setSlug] = useState("");
  const [copied, setCopied] = useState(false);
  const [editSlug, setEditSlug] = useState(null); // string or null when not editing
  const [savingSlug, setSavingSlug] = useState(false);

  const endpoint = kind === "customer"
    ? `${API}/doserne/projects/${projectId}/client-portal-customer`
    : `${API}/doserne/projects/${projectId}/client-portal`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(endpoint, authHeader());
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste status");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId, kind]);

  const createOrRotate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        endpoint,
        { ttl_days: Math.max(7, Math.min(365, Number(ttl) || 30)), slug: slug || "" },
        authHeader(),
      );
      setStatus(data);
      setSlug("");
      toast.success(status?.active ? "Ny lenke generert" : "Lenke opprettet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke generere lenke");
    }
    setBusy(false);
  };

  const revoke = async () => {
    if (!window.confirm("Sikker på at du vil tilbakekalle lenken? Kunden kan ikke bruke den lenger.")) return;
    setBusy(true);
    try {
      await axios.delete(endpoint, authHeader());
      setStatus({ active: false, token: null, customer: status?.customer });
      toast.info("Lenke tilbakekalt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke tilbakekalle");
    }
    setBusy(false);
  };

  const activeToken = status?.active ? status.token : null;
  const fullUrl = activeToken ? `${window.location.origin}${activeToken.url_path}` : "";
  const customerName = status?.customer?.name || "";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Kopiering mislyktes");
    }
  };

  const saveSlug = async () => {
    setSavingSlug(true);
    try {
      const { data } = await axios.patch(
        `${endpoint}/slug`,
        { slug: editSlug || "" },
        authHeader(),
      );
      setStatus((s) => ({ ...s, token: data.token }));
      setEditSlug(null);
      toast.success("Lenke oppdatert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere lenke");
    }
    setSavingSlug(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="client-portal-dialog">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-black">Kundeportal</div>
            <h2 className="text-xl font-black text-slate-900">Lenke til kunden</h2>
            <div className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">{projectAddress}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Kind switcher — per project or per customer */}
        <div className="px-5 pt-4">
          <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => setKind("project")}
              className={`px-3 py-2 rounded-md text-xs font-black transition ${kind === "project" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="portal-kind-project"
            >
              Kun dette prosjektet
            </button>
            <button
              onClick={() => setKind("customer")}
              className={`px-3 py-2 rounded-md text-xs font-black transition ${kind === "customer" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="portal-kind-customer"
            >
              Alle prosjekter{customerName ? ` · ${customerName.slice(0, 22)}${customerName.length > 22 ? "…" : ""}` : ""}
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : activeToken ? (
            <>
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-emerald-900">
                  {kind === "customer" ? (
                    <>Aktiv kundelenke — dekker <strong>alle prosjekter</strong> for {customerName || "denne kunden"}. Gyldig til <strong>{new Date(activeToken.expires_at).toLocaleDateString("no-NO", { day: "2-digit", month: "short", year: "numeric" })}</strong>.</>
                  ) : (
                    <>Aktiv lenke. Gyldig til <strong>{new Date(activeToken.expires_at).toLocaleDateString("no-NO", { day: "2-digit", month: "short", year: "numeric" })}</strong>.</>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">URL å sende</label>
                {editSlug === null ? (
                  <div className="flex gap-2 items-center">
                    <input readOnly value={fullUrl} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono bg-slate-50" data-testid="portal-url-input" />
                    <button
                      onClick={() => setEditSlug(activeToken.slug || "")}
                      className="w-9 h-9 rounded-lg border border-slate-300 hover:bg-slate-100 flex items-center justify-center text-slate-600"
                      title="Rediger lenke"
                      data-testid="portal-edit-slug-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={copyUrl}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-black"
                      style={{ color: "#fff" }}
                      data-testid="portal-copy-btn"
                    >
                      <Copy className="w-4 h-4" /> {copied ? "Kopiert!" : "Kopier"}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 items-center bg-cyan-50 border-2 border-cyan-300 rounded-lg p-2">
                    <span className="text-xs text-slate-500 font-mono flex-shrink-0 pl-1">.../klient/{kind === "customer" ? "c" : "p"}/</span>
                    <input
                      value={editSlug}
                      onChange={(e) => setEditSlug(e.target.value)}
                      placeholder="tilfeldig-token-om-tom"
                      autoFocus
                      className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono bg-white"
                      data-testid="portal-edit-slug-input"
                    />
                    <button
                      onClick={saveSlug}
                      disabled={savingSlug}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-black disabled:opacity-50"
                      style={{ color: "#fff" }}
                      data-testid="portal-slug-save-btn"
                    >
                      {savingSlug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Lagre
                    </button>
                    <button
                      onClick={() => setEditSlug(null)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-xs font-semibold"
                      data-testid="portal-slug-cancel-btn"
                    >
                      Avbryt
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <label className="text-xs font-semibold text-slate-700">Gyldighet (dager):</label>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-center"
                  data-testid="portal-ttl-input"
                />
                <button
                  onClick={createOrRotate}
                  disabled={busy}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-500 text-cyan-700 hover:bg-cyan-50 text-xs font-black disabled:opacity-50"
                  data-testid="portal-rotate-btn"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Regenerer
                </button>
                <button
                  onClick={revoke}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 text-xs font-black disabled:opacity-50"
                  data-testid="portal-revoke-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Tilbakekall
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                {kind === "customer" ? (
                  <>Generer én lenke som gir kunden <strong>{customerName || "kunden"}</strong> tilgang til <strong>alle sine prosjekter</strong> hos Doserne Stillas AS — signering, kontroll, SJA og fotoprogresjon.</>
                ) : (
                  <>Generer en visnings-lenke som du kan sende kunden for dette ene prosjektet. De ser detaljer, fotoprogresjon og alle underlag — inkludert de som venter på signatur.</>
                )}
              </p>
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-slate-700">Gyldighet (dager):</label>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-center"
                  data-testid="portal-ttl-input"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1.5">Egendefinert lenke (valgfritt)</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-mono flex-shrink-0">.../klient/{kind === "customer" ? "c" : "p"}/</span>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder={kind === "customer" ? "solideiendom" : "sinsen-18-20"}
                    className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
                    data-testid="portal-slug-input"
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Bare a-z, 0-9 og bindestrek. Blank = tilfeldig token.</p>
              </div>
              <button
                onClick={createOrRotate}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black"
                style={{ color: "#fff" }}
                data-testid="portal-create-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                {kind === "customer" ? "Generer kundelenke" : "Generer prosjekt-lenke"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ProjectHoursTab — admin view of all timesheet hours logged on this project:
// who worked, from/to, which days. Scope = this month or the whole project.
// -----------------------------------------------------------------------------
const _pad2 = (n) => String(n).padStart(2, "0");
const _thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}`; };
const _shiftMonth = (base, n) => { const [y, m] = base.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}`; };
const _fmtHrs = (h) => `${(h || 0).toFixed(2).replace(".", ",")} t`;
const _fmtDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}.${m}.${y}`; } catch { return iso; } };
const _MONTH_LABEL = (mm) => { const [y, m] = mm.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleDateString("nb-NO", { month: "long", year: "numeric" }); };

function ProjectHoursTab({ projectId, authHeader }) {
  const [scope, setScope] = useState("month");
  const [month, setMonth] = useState(_thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    axios.get(`${API}/doserne/timesheet/project/${projectId}/hours`, { params: { scope, month }, ...authHeader() })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [projectId, scope, month, authHeader]);

  return (
    <div className="space-y-4" data-testid="project-hours-tab">
      {/* Scope toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-xl bg-slate-100 p-1">
          <button onClick={() => setScope("month")} data-testid="hours-scope-month"
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${scope === "month" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Luna</button>
          <button onClick={() => setScope("all")} data-testid="hours-scope-all"
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${scope === "all" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Tot proiectul</button>
        </div>
        {scope === "month" && (
          <div className="inline-flex items-center gap-2">
            <button onClick={() => setMonth((m) => _shiftMonth(m, -1))} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="hours-prev-month"><ArrowLeft className="w-4 h-4" /></button>
            <span className="text-sm font-black text-slate-700 capitalize min-w-[130px] text-center" data-testid="hours-month-label">{_MONTH_LABEL(month)}</span>
            <button onClick={() => setMonth((m) => _shiftMonth(m, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 rotate-180" data-testid="hours-next-month"><ArrowLeft className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {busy ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
      ) : !data || data.total_minutes === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="hours-empty">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Nicio oră înregistrată {scope === "month" ? "în această lună" : "pe acest proiect"}.
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-cyan-200 bg-cyan-50/60 p-3">
              <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-black">Total ore</div>
              <div className="text-2xl font-black text-cyan-900 mt-0.5" data-testid="hours-total">{_fmtHrs(data.total_hours)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Muncitori</div>
              <div className="text-2xl font-black text-slate-800 mt-0.5">{data.worker_count}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Zile</div>
              <div className="text-2xl font-black text-slate-800 mt-0.5">{data.day_count}</div>
            </div>
          </div>

          {/* Per worker */}
          <div className="rounded-2xl border border-slate-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-2">Pe muncitor</div>
            <div className="space-y-1.5">
              {data.by_worker.map((w) => (
                <div key={w.email} className="flex items-center justify-between gap-2 text-sm" data-testid={`hours-worker-${w.email}`}>
                  <span className="font-bold text-slate-800 truncate">{w.name}</span>
                  <span className="text-slate-500 flex-shrink-0">{_fmtHrs(w.hours)} · {w.days} {w.days === 1 ? "zi" : "zile"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Per day */}
          <div className="space-y-2">
            {data.by_day.map((d) => (
              <div key={d.date} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`hours-day-${d.date}`}>
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-100">
                  <span className="text-sm font-black text-slate-700">{_fmtDate(d.date)}</span>
                  <span className="text-xs font-bold text-cyan-700">{_fmtHrs(d.hours)}</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {d.rows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="font-semibold text-slate-800 truncate flex-1">{r.name}</span>
                      <span className="font-mono text-slate-500 flex-shrink-0">{r.from_time}–{r.to_time}</span>
                      <span className="text-slate-700 font-bold w-16 text-right flex-shrink-0">{_fmtHrs(r.hours)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="text-[11px] text-slate-400 italic">Orele afișate sunt segmentele introduse (de la–până la). Pauza de masă se deduce la nivel de zi pe fluturaș.</div>
        </>
      )}
    </div>
  );
}


function DocumentList({ type, projectId, items, loading, Icon, title, isAdmin, authHeader, onDeleted }) {
  const createHref = CREATE_URLS[type](projectId);
  const openHref = OPEN_URLS[type];
  const [deletingId, setDeletingId] = useState(null);
  const [sjaBusyId, setSjaBusyId] = useState(null);

  const deleteUnderlag = async (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    const uid = d.underlag_id || d.id;
    if (!uid) return;
    if (!window.confirm(`Slette utkast #${d.number || ""}? Dette kan ikke angres.`)) return;
    setDeletingId(uid);
    try {
      await axios.delete(`${API}/underlag/admin/${uid}`, authHeader());
      toast.success("Slettet");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke slette");
    } finally {
      setDeletingId(null);
    }
  };

  // SJA is locked once "sent"/"finalized". Admin (the initiator) can reopen it
  // to keep collecting signatures on the same link, or send it (PDF to admin).
  const reopenSja = async (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    const sid = d.sja_id || d.id;
    if (!sid) return;
    setSjaBusyId(sid);
    try {
      await axios.post(`${API}/sja/doserne/${sid}/reopen`, {}, authHeader());
      toast.success("Gjenåpnet — nye signaturer kan legges til");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke gjenåpne");
    } finally {
      setSjaBusyId(null);
    }
  };

  const sendSja = async (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    const sid = d.sja_id || d.id;
    if (!sid) return;
    if (!window.confirm("Send SJA? PDF sendes til admin og lenken låses for nye signaturer.")) return;
    setSjaBusyId(sid);
    try {
      await axios.post(`${API}/sja/doserne/${sid}/send`, {}, authHeader());
      toast.success("SJA sendt");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende");
    } finally {
      setSjaBusyId(null);
    }
  };

  const summaryOf = (d) => {
    // Provide a compact summary line adapted to each doc type
    if (type === "underlag") {
      const date = d.date || d.created_at || "";
      const client = d.client_name || d.customer_name || d.customer || "";
      const activity = d.activity || d.work_description || "";
      return { primary: activity || client || "Underlag", secondary: [date?.slice(0, 10), client].filter(Boolean).join(" · ") };
    }
    if (type === "kontroll") {
      return { primary: d.title || d.name || "Kontroll", secondary: [d.date?.slice(0, 10), d.status].filter(Boolean).join(" · ") };
    }
    if (type === "sja") {
      return { primary: d.title || d.task || "SJA", secondary: [d.date?.slice(0, 10), d.risk_level].filter(Boolean).join(" · ") };
    }
    return { primary: d.id, secondary: "" };
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-slate-100">
        <Icon className="w-5 h-5 text-slate-500"/>
        <div className="flex-1">
          <div className="text-sm font-black text-slate-900">{title}</div>
          <div className="text-[11px] text-slate-500">
            {loading ? "Laster…" : `${items.length} document${items.length !== 1 ? "s" : ""} for this project`}
          </div>
        </div>
        <a href={createHref} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-black uppercase tracking-widest"
           data-testid={`create-new-${type}`}>
          <Plus className="w-3.5 h-3.5"/> Create new
        </a>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400"/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 px-4">
          <Icon className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
          <div className="text-sm font-black text-slate-700">No {type} documents yet</div>
          <div className="text-xs text-slate-500 mt-1">Tap <strong>Create new</strong> to add the first one.</div>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((d) => {
            const { primary, secondary } = summaryOf(d);
            const isSigned = d.status === "signed";
            const rowKey = d.id || d.underlag_id || d.edit_token;
            return (
              <a key={rowKey} href={openHref(d)} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50 active:bg-slate-100 transition"
                 data-testid={`doc-item-${type}-${rowKey}`}>
                {/* Number badge (Underlag only) */}
                {type === "underlag" && d.number != null && (
                  <div className={`flex-shrink-0 min-w-[52px] text-center px-2 py-1 rounded-lg font-mono font-black text-xs border ${isSigned ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
                    #{d.number}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-black text-slate-900 truncate">{primary}</span>
                    {type === "underlag" && (
                      isSigned ? (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5">
                          Signert
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-0.5">
                          Ikke signert
                        </span>
                      )
                    )}
                  </div>
                  {secondary && <div className="text-[11px] text-slate-500 truncate">{secondary}</div>}
                </div>
                {type === "underlag" && isAdmin && !isSigned && (
                  <button
                    type="button"
                    onClick={(e) => deleteUnderlag(e, d)}
                    disabled={deletingId === (d.underlag_id || d.id)}
                    className="flex-shrink-0 p-1.5 rounded-lg text-red-500 hover:bg-red-50 active:bg-red-100 disabled:opacity-40 transition"
                    title="Slett utkast"
                    data-testid={`delete-underlag-${d.number || rowKey}`}
                  >
                    {deletingId === (d.underlag_id || d.id)
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
                {type === "sja" && isAdmin && (() => {
                  const sid = d.sja_id || d.id;
                  const locked = d.status === "sent" || d.status === "finalized";
                  const busy = sjaBusyId === sid;
                  return locked ? (
                    <button
                      type="button"
                      onClick={(e) => reopenSja(e, d)}
                      disabled={busy}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-xs font-bold disabled:opacity-40 transition"
                      title="Gjenåpne for nye signaturer"
                      data-testid={`sja-reopen-${sid}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Lås opp
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => sendSja(e, d)}
                      disabled={busy}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 text-xs font-bold disabled:opacity-40 transition"
                      title="Send SJA (PDF til admin)"
                      data-testid={`sja-send-${sid}`}
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Send
                    </button>
                  );
                })()}
                <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0"/>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Admin-only: set a per-hour team-leader supplement (Tillegg bas) for this
// project and pick who receives it. Extra NOK/hour is added to the leader's
// payslip for every hour worked on this project.
function LeaderSupplementCard({ project, projectId, authHeader, onSaved }) {
  const [members, setMembers] = useState([]);
  const [rate, setRate] = useState(project.leader_supplement_rate || 0);
  const [emails, setEmails] = useState((project.leader_emails || []).map((e) => e.toLowerCase()));
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState((project.leader_supplement_rate || 0) > 0 || (project.leader_emails || []).length > 0);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/doserne/admin/members`, authHeader()); setMembers(data.members || []); }
      catch { /* ignore */ }
    })();
  }, [authHeader]);

  const toggle = (email) => setEmails((cur) => cur.includes(email) ? cur.filter((e) => e !== email) : [...cur, email]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { leader_supplement_rate: Number(rate) || 0, leader_emails: emails };
      await axios.patch(`${API}/doserne/projects/${projectId}`, payload, authHeader());
      onSaved?.(payload);
      toast.success("Tillegg bas lagret");
    } catch { toast.error("Kunne ikke lagre tillegg"); }
    finally { setBusy(false); }
  };

  const nameOf = (m) => m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="leader-supplement-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="leader-supplement-toggle">
        <UserIcon className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-black text-amber-800 flex-1">Tillegg bas / Supliment șef echipă</span>
        {(project.leader_supplement_rate || 0) > 0 && <span className="text-[11px] font-black text-amber-700">+{project.leader_supplement_rate} kr/t · {(project.leader_emails || []).length}</span>}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Supliment (NOK / oră)</label>
            <input type="number" min={0} step="0.5" value={rate} onChange={(e) => setRate(e.target.value)}
              className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" placeholder="25" data-testid="leader-supplement-rate" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Șefi de echipă (primesc suplimentul)</div>
            <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {members.length === 0 ? (
                <div className="px-3 py-3 text-xs text-slate-400 italic">Se încarcă…</div>
              ) : members.map((m) => {
                const checked = emails.includes(m.email.toLowerCase());
                return (
                  <button key={m.email} type="button" onClick={() => toggle(m.email.toLowerCase())}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-amber-50" data-testid={`leader-supplement-member-${m.email}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-amber-500 border-amber-500" : "border-slate-300 bg-white"}`}>
                      {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-slate-800 truncate">{nameOf(m)}</span>
                      <span className="block text-[10px] text-slate-400 truncate">{m.email}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-sm font-black text-white" data-testid="leader-supplement-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Lagre
          </button>
        </div>
      )}
    </div>
  );
}


// Admin-only: mark this project as the depot/warehouse. Hours logged here are
// always paid as normal (never 50% overtime) on weekdays/Saturday — Sundays &
// red days stay 100% overtime.
function NoOvertimeCard({ project, projectId, authHeader, onSaved }) {
  const [busy, setBusy] = useState(false);
  const active = !!project.no_overtime;

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !active;
      await axios.patch(`${API}/doserne/projects/${projectId}`, { no_overtime: next }, authHeader());
      onSaved?.({ no_overtime: next });
      toast.success(next ? "Marcat ca depozit (fără overtime)" : "Overtime reactivat");
    } catch { toast.error("Kunne ikke lagre"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-4" data-testid="no-overtime-card">
      <button onClick={toggle} disabled={busy} className="w-full flex items-center gap-2.5 text-left disabled:opacity-50" data-testid="no-overtime-toggle">
        <span className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${active ? "bg-sky-500 border-sky-500" : "border-slate-300 bg-white"}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin text-sky-600" /> : active && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-black text-sky-800">Depozit — fără overtime</span>
          <span className="block text-[11px] text-slate-500 leading-tight mt-0.5">
            Orele pontate aici se plătesc mereu normal (fără OT 1.5×) și NU intră în pragul de 37.5h/săpt. Duminica și zilele roșii rămân OT 2×. Marchează DOAR Lager Doserne Stillas; Lager Doserne SALG rămâne proiect normal (cu overtime).
          </span>
        </span>
      </button>
    </div>
  );
}


export default function DoserneProjectDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromPlan = searchParams.get("from_plan");
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState({ underlags: [], kontrolls: [], sjas: [] });
  const [planPhotos, setPlanPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") || "info");
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [liveArea, setLiveArea] = useState(null);
  const [planLang, setPlanLang] = usePlanLang(authHeader);
  const [descTr, setDescTr] = useState(null);

  // Translate the project description into the worker's chosen language.
  useEffect(() => {
    let alive = true;
    const desc = data?.project?.description;
    if (!planLang || !desc || !desc.trim()) { setDescTr(null); return; }
    translateContent(desc, planLang, authHeader).then((txt) => { if (alive) setDescTr(txt); });
    return () => { alive = false; };
  }, [planLang, data?.project?.description, authHeader]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const { data } = await axios.get(`${API}/doserne/projects/${id}`, authHeader());
        setData(data);
      } catch { toast.error("Kunne ikke laste prosjekt"); }
      setLoading(false);
    })();
  }, [id, user]);

  // Load all documents linked to this project once (used by all three doc tabs)
  const loadDocs = useCallback(async () => {
    if (!user) return;
    setDocsLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/projects/${id}/documents`, authHeader());
      setDocs(data);
    } catch { /* silent — 404 if collection empty */ }
    setDocsLoading(false);
  }, [id, user, authHeader]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Load plan-attached photos (shown at top of Progresjon tab)
  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const { data } = await axios.get(`${API}/doserne/projects/${id}/plan-photos`, authHeader());
        setPlanPhotos(data.photos || []);
      } catch { /* silent */ }
    })();
  }, [id, user]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  const p = data?.project;
  const counts = data?.counts || {};

  return (
    <DoserneShell>
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => {
            // Prefer real browser history so we return to daily plan / chat /
            // notification / wherever the admin came from. Fall back to the
            // project list on cold loads (no history entry).
            if (window.history.length > 1) navigate(-1);
            else navigate("/doserne/prosjekter");
          }}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded-lg"
          data-testid="project-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Tilbake
        </button>
        {fromPlan && (
          <Link to="/doserne/plan" className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-900 px-2 py-1 bg-sky-50 border border-sky-200 rounded-lg" data-testid="from-plan-back">
            Daglig plan
          </Link>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : !p ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">Prosjekt ikke funnet.</div>
      ) : (
        <>
          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                {p.number && <div className="text-[10px] uppercase font-mono tracking-wider text-slate-500">{p.number}</div>}
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight" data-testid="project-detail-title">
                  {p.address || p.name || "(uten adresse)"}
                </h1>
                {p.name && p.name !== p.address && (
                  <div className="text-sm text-slate-500 mt-0.5">{p.name}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {user.is_admin && (
                  <button
                    onClick={() => setPortalDialogOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-100 hover:bg-cyan-200 text-cyan-800 text-xs font-black border border-cyan-300"
                    data-testid="open-client-portal-btn"
                    title="Trimite link către client"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Link client
                  </button>
                )}
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("doserne:open-project-chat", { detail: { project_id: id } }));
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black"
                  style={{ color: "#fff" }}
                  data-testid="project-chat-btn"
                  title="Åpne prosjekt-chat"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Chat
                </button>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 border border-slate-200 rounded-full px-2.5 py-1">
                  {p.status || "active"}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
              <KV Icon={UserIcon} label="Kunde" value={p.customer_name} />
              <AddressField value={p.address} projectId={id} isAdmin={user.is_admin} authHeader={authHeader}
                onSaved={(addr) => setData((d) => ({ ...d, project: { ...d.project, address: addr } }))} />
              <KV Icon={Mail}     label="E-post" value={p.customer_email} />
              <KV Icon={Phone}    label="Telefon" value={p.customer_phone} />
              <KV Icon={Calendar} label="Start"  value={p.start_date} />
              <KV Icon={Calendar} label="Slutt"  value={p.end_date} />
            </div>
            {p.description && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Beskrivelse</span>
                  <FlagBar value={planLang} onChange={setPlanLang} />
                </div>
                {descTr && (
                  <div className="inline-flex items-center gap-1 mb-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide">
                    {FLAG_LABEL[planLang] || planLang}
                  </div>
                )}
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{descTr || p.description}</div>
              </div>
            )}

            {user.is_admin && (
              <div className="mt-4">
                <AdminDocsBox
                  docs={p.admin_docs || []}
                  authHeader={authHeader}
                  onChange={async (newDocs) => {
                    setData((d) => ({ ...d, project: { ...d.project, admin_docs: newDocs } }));
                    try { await axios.patch(`${API}/doserne/projects/${id}`, { admin_docs: newDocs }, authHeader()); }
                    catch { toast.error("Kunne ikke lagre dokumenter"); }
                  }}
                  title="Tilbud & dokumenter (kun admin)"
                />
                <div className="mt-4">
                  <LeaderSupplementCard
                    project={p}
                    projectId={id}
                    authHeader={authHeader}
                    onSaved={(payload) => setData((d) => ({ ...d, project: { ...d.project, ...payload } }))}
                  />
                </div>
                <NoOvertimeCard
                  project={p}
                  projectId={id}
                  authHeader={authHeader}
                  onSaved={(payload) => setData((d) => ({ ...d, project: { ...d.project, ...payload } }))}
                />
              </div>
            )}

            {/* Quick counters */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-5 pt-5 border-t border-slate-100">
              {[
                { key: "total_area",    label: "Areal m²",   value: (() => { const a = liveArea != null ? liveArea : (counts.total_area_m2 ?? 0); return a ? Math.round(a * 100) / 100 : 0; })(), color: "text-fuchsia-600" },
                { key: "underlags",     label: "Underlag",   value: counts.underlags,     color: "text-slate-700" },
                { key: "kontrolls",     label: "Kontroll",   value: counts.kontrolls,     color: "text-blue-600" },
                { key: "sjas",          label: "SJA",        value: counts.sjas,          color: "text-orange-600" },
                { key: "progress_days", label: "Dager",      value: counts.progress_days, color: "text-emerald-600" },
                { key: "photos",        label: "Bilder",     value: counts.photos,        color: "text-cyan-600" },
              ].map((c) => (
                <div key={c.key} className="text-center" data-testid={`counter-${c.key}`}>
                  <div className={`text-xl font-black ${c.color}`}>{c.value ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-200 mb-4 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {TABS.filter((t) => !t.adminOnly || user.is_admin).map((t) => {
                const active = tab === t.key;
                const cls = `inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 transition ${active ? "border-cyan-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} className={cls} data-testid={`tab-${t.key}`}>
                    <t.Icon className="w-4 h-4" /> {t.label}
                    {t.key === "underlag" && counts.underlags > 0 && <span className="ml-1 text-[10px] font-mono bg-slate-100 text-slate-600 rounded-full px-1.5">{counts.underlags}</span>}
                    {t.key === "kontroll" && counts.kontrolls > 0 && <span className="ml-1 text-[10px] font-mono bg-blue-100 text-blue-600 rounded-full px-1.5">{counts.kontrolls}</span>}
                    {t.key === "sja" && counts.sjas > 0 && <span className="ml-1 text-[10px] font-mono bg-orange-100 text-orange-600 rounded-full px-1.5">{counts.sjas}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          {tab === "info" && (
            <DoserneProjectJobs projectId={id} isAdmin={user.is_admin} authHeader={authHeader} onTotalArea={setLiveArea} />
          )}
          {tab === "progress" && (
            <div className="space-y-4">
              {/* Quick action — big "Add photos" button always at the top of Progresjon */}
              <a
                href={`/doserne/progresjon?project_id=${encodeURIComponent(id)}`}
                className="flex items-center gap-3 bg-cyan-600 hover:bg-cyan-700 rounded-2xl p-4 shadow-sm hover:shadow-md transition"
                style={{ color: "#fff" }}
                data-testid="progress-add-photos-btn"
              >
                <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black">Legg til bilder / notat</div>
                  <div className="text-xs opacity-90">Opp til 10 bilder per innlegg — velg dato og legg til fra kamera eller galleri</div>
                </div>
                <Plus className="w-5 h-5 flex-shrink-0" />
              </a>
              {planPhotos.length > 0 && (
                <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-4" data-testid="plan-photos-section">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-lg bg-sky-500 text-white flex items-center justify-center">
                      <Camera className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-black text-sky-700">
                      Fra Daglig Plan · {planPhotos.length} bilde{planPhotos.length === 1 ? "" : "r"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {planPhotos.map((ph) => (
                      <a
                        key={ph.id}
                        href={ph.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-sky-300 hover:border-sky-500 hover:scale-[1.02] transition group"
                        title={`${ph.label || ""} · ${ph.plan_date || ""}`}
                        data-testid={`plan-photo-${ph.id}`}
                      >
                        <img src={ph.url} alt={ph.label || ""} className="w-full h-full object-cover" loading="lazy" />
                        {ph.label && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1 py-0.5">
                            <div className="text-[9px] font-black text-white truncate">{ph.label}</div>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <DoserneProgressTimeline projectId={id} authHeader={authHeader} currentUserId={user.id} isAdmin={user.is_admin} />
            </div>
          )}
          {tab === "lister" && (
            <ProjectMaterialListsTab projectId={id} authHeader={authHeader} isAdmin={user.is_admin} />
          )}
          {tab === "underlag" && (
            <DocumentList type="underlag" projectId={id} items={docs.underlags} loading={docsLoading}
                          Icon={FileText} title="Underlag / Timesheet"
                          isAdmin={user.is_admin} authHeader={authHeader} onDeleted={loadDocs}/>
          )}
          {tab === "kontroll" && (
            <DocumentList type="kontroll" projectId={id} items={docs.kontrolls} loading={docsLoading}
                          Icon={ClipboardList} title="Kontroll"/>
          )}
          {tab === "sja" && (
            <DocumentList type="sja" projectId={id} items={docs.sjas} loading={docsLoading}
                          Icon={ShieldAlert} title="SJA"
                          isAdmin={user.is_admin} authHeader={authHeader} onDeleted={loadDocs}/>
          )}
          {tab === "timer" && user.is_admin && (
            <ProjectHoursTab projectId={id} authHeader={authHeader} />
          )}
        </>
      )}

      {portalDialogOpen && (
        <ClientPortalDialog
          projectId={id}
          projectAddress={data?.project?.address || data?.project?.name || ""}
          onClose={() => setPortalDialogOpen(false)}
          authHeader={authHeader}
        />
      )}
    </DoserneShell>
  );
}
